import { Card, Checkbox, Col, Flex, Row } from 'antd';
import { useMemo } from 'react';
import CustomTitle from '../../components/CustomTitle';
import CustomParagraph from '../../components/CustomParagraph';
import LocalShipping from '../../assets/images/20road.png';
import AirplaneFlying from '../../assets/images/20air.png';
import { capitalize } from '../../common/helper';
import StatusTag from './components/StatusTag';
import { StatusType } from '@/pages/Shipments/statusType';
import TruncateWithTooltip from '@/components/TruncateWithTooltip';
import { getShipmentLocationParsedAddress } from '@/common/utils/utils';
import TeamListWithDots from './components/TeamListWithDots';
import { EllipsisTooltip } from '@/components';

interface Location {
  name: string;
  parsed_address?: {
    PlaceName?: string;
    StateName?: string;
    ZipCode?: string;
    Recipient?: string;
  };
}

interface Team {
  name: string;
  color: string;
}

interface Leg {
  distribution: number;
  status: string;
  mode: string;
}

const normalizeShipmentLegs = (status: string, legs?: Leg[]): Leg[] => {
  if (!legs?.length) return [];

  const normalizedStatus = (status || '').toUpperCase().replace(/-/g, '_');
  if (normalizedStatus === 'DELIVERED') {
    return legs.map((leg) => ({ ...leg, status: 'travelled' }));
  }
  if (
    ['NOT_FOUND', 'AWAITING_PICKUP', 'PENDING', 'DATA_LOADING'].includes(
      normalizedStatus
    )
  ) {
    return legs.map((leg) => ({ ...leg, status: 'pending' }));
  }
  if (['IN_TRANSIT', 'OUT_FOR_DELIVERY'].includes(normalizedStatus)) {
    if (legs.length === 1) {
      return [{ ...legs[0], status: 'travelled' }];
    }
    return legs.map((leg, index) => ({
      ...leg,
      status: index < legs.length - 1 ? 'travelled' : 'pending',
    }));
  }
  return legs;
};

const getLegIcon = (mode?: string) => {
  const normalizedMode = (mode || '').toLowerCase();
  if (['truck', 'road', 'ground', 'van'].includes(normalizedMode)) {
    return LocalShipping;
  }
  return AirplaneFlying;
};

export interface ShipmentCardData {
  id: string;
  reference_number: string;
  status: string;
  alert_status: string[];
  type: StatusType;
  from_location: Location;
  to_location: Location;
  scheduled_pickup_date: string;
  actual_pickup_date: string;
  scheduled_delivery_date: string;
  actual_delivery_date: string;
  courier: {
    title: string;
  };
  teams: Team[];
  legs_info?: {
    legs: Leg[];
  };
  is_leg?: boolean;
  leg_type?: string;
}

export interface DrawerState {
  isOpen: boolean;
}

interface ShipmentCardProps {
  checked?: boolean;
  selected?: boolean;
  onSelectChange?: (
    id: string,
    checked: boolean,
    card: ShipmentCardData
  ) => void;
  card: ShipmentCardData;
  toggleDrawer: (
    action: string,
    type: string,
    id: string,
    alert?: unknown,
    legType?: 'CL' | 'EL'
  ) => void;
  openShipmentId: string | null;
  drawerState: DrawerState;
  highlighted?: boolean;
}

/**
 * ShipmentCard Component
 *
 * Displays shipment information including reference number, status, locations,
 * team assignments, courier info, and progress visualization. Supports selection
 * via checkbox and opens detailed view when clicked.
 */
function ShipmentCard({
  checked,
  selected,
  onSelectChange,
  card,
  toggleDrawer,
  openShipmentId,
  drawerState,
  highlighted,
}: ShipmentCardProps) {
  const isCardOpen = openShipmentId === card?.id;
  const isCardActive = highlighted || isCardOpen;
  const legs = useMemo(
    () => normalizeShipmentLegs(card?.status, card?.legs_info?.legs),
    [card?.status, card?.legs_info?.legs]
  );

  return (
    <>
      <Card
        style={{
          borderRadius: '12px',
          border: '2px solid',
          cursor: 'pointer',
          height: 'fit-content',
          borderColor: isCardActive ? '#266EF1' : '#EEE',
        }}
        bodyStyle={{ padding: '15px 15px' }}
        onClick={() => {
          const legType = card?.is_leg ? 'EL' : 'CL';
          toggleDrawer('open', 'Detail', card?.id, undefined, legType);
        }}
        data-testid='shipment-card'
      >
        <Row
          gutter={[24, 16]}
          style={{ marginBottom: '0' }}
          justify='space-between'
        >
          <Col
            xxl={drawerState.isOpen ? 4 : 4}
            xl={drawerState.isOpen ? 4 : 4}
            lg={drawerState.isOpen ? 24 : 5}
            md={drawerState.isOpen ? 24 : 24}
            sm={drawerState.isOpen ? 24 : 24}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              {checked && (
                <div style={{ paddingTop: '0px', width: '20px' }}>
                  <div
                    onClick={(e) => e.stopPropagation()}
                    style={{ paddingTop: '18px', width: '20px' }}
                  >
                    <Checkbox
                      className='custom-afaf-checkbox'
                      checked={selected}
                      onChange={(e) => {
                        onSelectChange?.(card.id, e.target.checked, card);
                      }}
                    />
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <TruncateWithTooltip
                  text={card?.reference_number}
                  max={18}
                  render={(t) => (
                    <span style={{ width: 'fit-content' }}>
                      <CustomTitle
                        text={t}
                        fontSize={12}
                        fontWeight={600}
                        lineHeight='20px'
                        color='var(--text-primary)'
                        marginBottom='4px'
                        className='ellipsis-multiline'
                      />
                    </span>
                  )}
                />

                <StatusTag
                  type={card?.type}
                  status={card?.status}
                  alert_status={card?.alert_status}
                />
              </div>
            </div>
          </Col>
          <Col
            xxl={drawerState.isOpen ? 7 : 7}
            xl={drawerState.isOpen ? 7 : 7}
            lg={drawerState.isOpen ? 10 : 8}
            md={drawerState.isOpen ? 24 : 12}
            sm={drawerState.isOpen ? 24 : 12}
            xs={drawerState.isOpen ? 24 : 24}
          >
            <TruncateWithTooltip
              text={
                card?.from_location?.name === 'Unknown Location'
                  ? 'Origin'
                  : card?.from_location?.name
              }
              max={20}
              disableTooltip={card?.from_location?.name === 'Unknown Location'}
              render={(t) => (
                <span style={{ width: 'fit-content', display: 'inline-block' }}>
                  <CustomTitle
                    text={t}
                    fontSize={12}
                    fontWeight={500}
                    lineHeight='20px'
                    color='var(--text-primary)'
                    marginBottom='0px'
                    style={{
                      width: 'fit-content',
                      minWidth: 1,
                      display: 'inline-block',
                    }}
                  />
                </span>
              )}
            />
            <EllipsisTooltip
              text={getShipmentLocationParsedAddress(card?.from_location)}
              className='font-12 fw-400 shipment-card-subtitle'
            />

            <CustomParagraph
              text={card?.actual_pickup_date || card?.scheduled_pickup_date}
              fontSize='12px'
              fontWeight={400}
              color='var(--text-light)'
              marginBottom='4px'
            />
          </Col>
          <Col
            xxl={drawerState.isOpen ? 7 : 7}
            xl={drawerState.isOpen ? 7 : 7}
            lg={drawerState.isOpen ? 10 : 8}
            md={drawerState.isOpen ? 24 : 12}
            sm={drawerState.isOpen ? 24 : 12}
            xs={drawerState.isOpen ? 24 : 24}
            style={{ minWidth: 0 }}
          >
            <Flex
              className='shipment-location'
              vertical
              style={{
                width: '100%',
                minWidth: 0,
              }}
            >
              {/* Title */}
              <TruncateWithTooltip
                text={
                  card?.to_location?.name === 'Unknown Location'
                    ? 'Destination'
                    : card?.to_location?.name
                }
                max={20}
                disableTooltip={card?.to_location?.name === 'Unknown Location'}
                render={(t) => (
                  <span
                    style={{
                      width: '100%',
                      minWidth: 0,
                      display: 'block',
                    }}
                  >
                    <CustomTitle
                      text={t}
                      fontSize={12}
                      fontWeight={500}
                      lineHeight='20px'
                      color='var(--text-primary)'
                      marginBottom='0px'
                      style={{
                        width: '100%',
                        minWidth: 0,
                        display: 'block',
                      }}
                    />
                  </span>
                )}
              />

              {/* Address (ELLIPSIS FIXED) */}
              <EllipsisTooltip
                text={getShipmentLocationParsedAddress(card?.to_location)}
                className='font-12 fw-400 shipment-card-subtitle'
              />

              {/* Date */}
              <CustomParagraph
                text={
                  card?.actual_delivery_date || card?.scheduled_delivery_date
                }
                fontSize='12px'
                fontWeight={400}
                color='var(--text-light)'
                marginBottom='4px'
                style={{
                  width: '100%',
                  minWidth: 0,
                  display: 'block',
                }}
              />
            </Flex>
          </Col>
        </Row>

        <Row gutter={[24, 16]} className='shipment-progress'>
          <Col
            xxl={drawerState.isOpen ? 7 : 7}
            xl={drawerState.isOpen ? 7 : 7}
            lg={drawerState.isOpen ? 24 : 7}
            md={drawerState.isOpen ? 24 : 24}
            sm={drawerState.isOpen ? 24 : 24}
            style={{ paddingLeft: checked ? '42px' : '12px' }}
          >
            <Flex gap={8} style={{ marginTop: '4px', flexWrap: 'wrap' }}>
              {card?.teams?.length > 0 && (
                <TeamListWithDots teams={card.teams} />
              )}
              <CustomParagraph
                text={capitalize(card?.courier?.title) || ''}
                fontSize='12px'
                fontWeight={500}
                color='var(--gray-600)'
                marginBottom='4px'
              />
            </Flex>
          </Col>

          <Col
            xxl={drawerState.isOpen ? 17 : 17}
            xl={drawerState.isOpen ? 17 : 17}
            lg={drawerState.isOpen ? 24 : 17}
            md={drawerState.isOpen ? 24 : 24}
            sm={drawerState.isOpen ? 24 : 24}
          >
            <Flex align='center' style={{ height: '100%', marginLeft: '8px' }}>
              {legs && legs.length > 0 ? (
                <ul className='custom-steps-display'>
                  {legs.map((leg, index) => {
                    const w = leg?.distribution;
                    return (
                      <li
                        key={index}
                        className={leg.status === 'travelled' ? 'active' : ''}
                        style={{ width: `${w}%`, marginRight: '4px' }} // Fixed template literal
                      >
                        {leg.status === 'travelled' && (
                          <div
                            className='centerImage'
                            style={{
                              width: '24px',
                              paddingLeft: '2px',
                              paddingRight: '2px',
                              backgroundColor: 'white',
                            }}
                          >
                            <img src={getLegIcon(leg.mode)} alt='' />
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <ul className='custom-steps-display'>
                  <li style={{ width: '100%' }}></li>
                </ul>
              )}
            </Flex>
          </Col>
        </Row>
      </Card>
    </>
  );
}

export default ShipmentCard;
